import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import { FiLinkedin, FiMail, FiDownload, FiUpload, FiRotateCcw } from "react-icons/fi";

export default function About({ resetData, exportData, importData }) {
  const inputFile = useRef(null);
  const history = useHistory();
  const [importSpinnerState, setImportSpinnerState] = useState(false);
  const [exportSpinnerState, setExportSpinnerState] = useState(false);

  // Reads a previously exported JSON backup and restores it into IndexedDB.
  function handleChange(e) {
    const fileReader = new FileReader();
    fileReader.readAsText(e.target.files[0], "UTF-8");
    fileReader.onload = (e) => {
      const JSONData = JSON.parse(e.target.result);
      importData(JSONData, () => {
        setImportSpinnerState(false);
        history.push("/");
      });
    };
  }

  return (
    <>
      <div className="page-header">
        <div className="page-emoji" role="img" aria-label="nest">
          🪺
        </div>
        <h1 className="page-title">About Algonest</h1>
        <p className="page-sub">Practice problems, apply to jobs, and track it all in one place.</p>
      </div>

      <div className="nb-callout">
        <div className="nb-callout-emoji" role="img" aria-label="sparkles">
          ✨
        </div>
        <div className="nb-callout-body">
          <p>
            <b>Algonest</b> brings your coding-interview prep into one calm place: work through{" "}
            <b>practice problems</b> organised by topic, mark what’s done, flag what to revise, bookmark
            the tricky ones and keep notes — then line up the <b>companies you want to apply to</b>, all
            without juggling spreadsheets and a dozen browser tabs.
          </p>
          <p>
            <b>Sign in — it’s free — to track your progress</b> and have it sync across every device
            (phone, laptop, anywhere). You can look around without an account; signing in simply keeps
            your progress saved and in sync. No ads, ever.
          </p>
          <p className="nb-fineprint">
            Privacy, honestly: the only things this site records for its owner are anonymous page
            views and, if you create an account, your email address — used purely to keep the project
            running and to share occasional updates. Your problem progress and notes are never sold or
            shared. Use the <b>suggestion button</b> (bottom-right) to send feedback any time.
          </p>
        </div>
      </div>

      <div className="section-title">What you can do</div>
      <div className="nb-rows">
        <div className="nb-row">
          <span className="nb-row-ico" role="img" aria-label="check">
            ✅
          </span>
          <span className="nb-row-label">Track progress</span>
          <span className="nb-row-val">Done · revise · bookmark · notes</span>
        </div>
        <div className="nb-row">
          <span className="nb-row-ico" role="img" aria-label="target">
            🎯
          </span>
          <span className="nb-row-label">Practice mode</span>
          <span className="nb-row-val">Random problem by topic &amp; difficulty</span>
        </div>
        <div className="nb-row">
          <span className="nb-row-ico" role="img" aria-label="link">
            🔗
          </span>
          <span className="nb-row-label">LeetCode links</span>
          <span className="nb-row-val">Added wherever a free equivalent exists</span>
        </div>
        <div className="nb-row">
          <span className="nb-row-ico" role="img" aria-label="briefcase">
            💼
          </span>
          <span className="nb-row-label">Apply tracker</span>
          <span className="nb-row-val">660+ companies · applied · cold-DM · notes</span>
        </div>
      </div>

      <div className="nb-note">
        <b>About difficulty:</b> only problems with a genuine free LeetCode match carry an official
        Easy / Medium / Hard label — the rest are intentionally left as <b>No level</b>. Filtering by
        difficulty (here and in Practice) therefore hides the untagged problems, so prefer <b>All</b>{" "}
        unless you specifically want the labelled ones. You can always set a level yourself by clicking
        a problem’s difficulty pill.
      </div>

      <div className="section-title">Your data</div>
      <div className="nb-toolbar">
        <button
          type="button"
          className="nb-btn"
          onClick={() => {
            setExportSpinnerState(true);
            exportData(() => setExportSpinnerState(false));
          }}
        >
          <FiDownload size={15} /> Export progress
          {exportSpinnerState ? <Spinner animation="border" size="sm" /> : null}
        </button>
        <button
          type="button"
          className="nb-btn"
          onClick={() => {
            setImportSpinnerState(true);
            inputFile.current.click();
          }}
        >
          <FiUpload size={15} /> Import progress
          {importSpinnerState ? <Spinner animation="border" size="sm" /> : null}
        </button>
        <button
          type="button"
          className="nb-btn nb-btn-danger"
          onClick={() => {
            if (window.confirm("Reset all progress? This cannot be undone — export a backup first.")) {
              resetData();
            }
          }}
        >
          <FiRotateCcw size={15} /> Reset progress
        </button>
        <input type="file" id="file" ref={inputFile} style={{ display: "none" }} accept=".json" onChange={handleChange} />
      </div>

      <div className="section-title">Contact</div>
      <div className="nb-rows">
        <a className="nb-row" href="https://www.linkedin.com/in/souravjha1111/" target="_blank" rel="noopener noreferrer">
          <span className="nb-row-ico">
            <FiLinkedin size={17} />
          </span>
          <span className="nb-row-label">LinkedIn</span>
          <span className="nb-row-val">in/souravjha1111</span>
        </a>
        <a className="nb-row" href="mailto:souravjha9599@gmail.com">
          <span className="nb-row-ico">
            <FiMail size={17} />
          </span>
          <span className="nb-row-label">Email</span>
          <span className="nb-row-val">souravjha9599@gmail.com</span>
        </a>
      </div>

      <p className="nb-built">
        Built with care by <b>Sourav Jha</b>.
      </p>
    </>
  );
}
